<script setup>
// NOTICE: KAPTION-INTEGRATION
// Test page for WhatsApp preview component - for development/debugging only
import { ref, computed } from 'vue';
import KaptionWhatsAppPreview from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreview.vue';

// Sample template for testing
const sampleTemplate = ref({
  name: 'test_template',
  language: 'en',
  category: 'UTILITY',
  components: [
    {
      type: 'BODY',
      text: 'Hello {{1}}! Your appointment is scheduled for {{2}}. Please arrive 15 minutes early. If you need to reschedule, please contact us at {{3}}. Thank you for choosing our service!',
    },
    {
      type: 'FOOTER',
      text: 'Sent via Kaption',
    },
    {
      type: 'BUTTONS',
      buttons: [
        { type: 'QUICK_REPLY', text: 'Confirm' },
        { type: 'QUICK_REPLY', text: 'Reschedule' },
      ],
    },
  ],
});

const renderedBody = ref(
  'Hello John! Your appointment is scheduled for Monday at 2pm. Please arrive 15 minutes early. If you need to reschedule, please contact us at +1-555-0123. Thank you for choosing our service!'
);

const bareMode = ref(true);
const loading = ref(false);
const showTemplate = ref(true);

const currentTemplate = computed(() =>
  showTemplate.value ? sampleTemplate.value : null
);

const toggleTheme = () => {
  document.body.classList.toggle('dark');
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="min-h-screen p-8 bg-n-background">
    <div class="max-w-4xl mx-auto">
      <h1 class="text-2xl font-bold text-n-slate-12 mb-6">
        WhatsApp Preview Test Page
      </h1>

      <!-- Controls -->
      <div class="flex gap-4 mb-8 p-4 bg-n-solid-2 rounded-lg">
        <button
          class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          :class="
            bareMode
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-black2 text-n-slate-11'
          "
          @click="bareMode = true"
        >
          Bare Mode (Message Only)
        </button>
        <button
          class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          :class="
            !bareMode
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-black2 text-n-slate-11'
          "
          @click="bareMode = false"
        >
          Phone Frame Mode
        </button>
        <button
          class="px-4 py-2 rounded-lg text-sm font-medium bg-n-alpha-black2 text-n-slate-11"
          @click="toggleTheme"
        >
          Toggle Theme
        </button>
        <button
          class="px-4 py-2 rounded-lg text-sm font-medium bg-n-alpha-black2 text-n-slate-11"
          @click="loading = !loading"
        >
          {{ loading ? 'Stop Loading' : 'Show Loading' }}
        </button>
        <button
          class="px-4 py-2 rounded-lg text-sm font-medium bg-n-alpha-black2 text-n-slate-11"
          @click="showTemplate = !showTemplate"
        >
          {{ showTemplate ? 'Hide Template' : 'Show Template' }}
        </button>
      </div>

      <!-- Preview Container with different backgrounds to test blending -->
      <div class="grid grid-cols-2 gap-8">
        <!-- On solid-1 background -->
        <div>
          <h2 class="text-sm font-medium text-n-slate-11 mb-2">
            On Solid Background (bg-n-solid-1) → gradient-var: --solid-1
          </h2>
          <div class="bg-n-solid-1 p-4 rounded-lg border border-n-weak">
            <div class="h-[400px]">
              <KaptionWhatsAppPreview
                :template="currentTemplate"
                :rendered-body="renderedBody"
                :bare="bareMode"
                :loading="loading"
                gradient-var="--solid-1"
                company-name="Test Business"
              />
            </div>
          </div>
        </div>

        <!-- On solid-2 background -->
        <div>
          <h2 class="text-sm font-medium text-n-slate-11 mb-2">
            On Card Background (bg-n-solid-2) → gradient-var: --solid-2
          </h2>
          <div class="bg-n-solid-2 p-4 rounded-lg border border-n-weak">
            <div class="h-[400px]">
              <KaptionWhatsAppPreview
                :template="currentTemplate"
                :rendered-body="renderedBody"
                :bare="bareMode"
                :loading="loading"
                gradient-var="--solid-2"
                company-name="Test Business"
              />
            </div>
          </div>
        </div>

        <!-- On slate-1 background -->
        <div>
          <h2 class="text-sm font-medium text-n-slate-11 mb-2">
            On Slate Background (bg-n-slate-1) → gradient-var: --slate-1
          </h2>
          <div class="bg-n-slate-1 p-4 rounded-lg border border-n-weak">
            <div class="h-[400px]">
              <KaptionWhatsAppPreview
                :template="currentTemplate"
                :rendered-body="renderedBody"
                :bare="bareMode"
                :loading="loading"
                gradient-var="--slate-1"
                company-name="Test Business"
              />
            </div>
          </div>
        </div>

        <!-- On slate-2 background -->
        <div>
          <h2 class="text-sm font-medium text-n-slate-11 mb-2">
            On Slate Background (bg-n-slate-2) → gradient-var: --slate-2
          </h2>
          <div class="bg-n-slate-2 p-4 rounded-lg border border-n-weak">
            <div class="h-[400px]">
              <KaptionWhatsAppPreview
                :template="currentTemplate"
                :rendered-body="renderedBody"
                :bare="bareMode"
                :loading="loading"
                gradient-var="--slate-2"
                company-name="Test Business"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- CSS Variable Debug -->
      <div class="mt-8 p-4 bg-n-solid-2 rounded-lg">
        <h2 class="text-sm font-medium text-n-slate-11 mb-4">
          CSS Variable Debug
        </h2>
        <div class="grid grid-cols-4 gap-4 text-xs">
          <div>
            <div class="w-full h-8 bg-[rgb(var(--solid-1))] rounded mb-1" />
            <code class="text-n-slate-10">--solid-1</code>
          </div>
          <div>
            <div class="w-full h-8 bg-[rgb(var(--solid-2))] rounded mb-1" />
            <code class="text-n-slate-10">--solid-2</code>
          </div>
          <div>
            <div class="w-full h-8 bg-[rgb(var(--slate-1))] rounded mb-1" />
            <code class="text-n-slate-10">--slate-1</code>
          </div>
          <div>
            <div class="w-full h-8 bg-[rgb(var(--slate-2))] rounded mb-1" />
            <code class="text-n-slate-10">--slate-2</code>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- eslint-enable vue/no-bare-strings-in-template -->
</template>
